/**
 * `<TaskOutlineRail/>` — the left "OUTLINE" rail beside the canvas (B2.1).
 *
 * Issue #759 (Phase 2). A compact vertical list of every node: a status
 * dot, the title, indented by depth. In PR-1 the rail is **select-only** —
 * clicking a row focuses that node on the canvas (selection is shared
 * through `treeStore`). Full keyboard EDITING of the outline (Enter =
 * sibling, Tab = indent, two-way sync) is PR-2; the seam is the
 * `onSelect` contract here, which PR-2 extends with edit handlers without
 * disturbing this select-only behaviour.
 *
 * The "+ sub-task" affordance at the foot adds a child to the focused node
 * (or a root when nothing is focused) — a light capture entry that doesn't
 * require the focused-card path.
 *
 * Editorial revamp (#1043, Wave 1): theme-aware. Editorial = the wireframe
 * `OutlinePane` (warm-paper rail, hairline `rule` right border, mono
 * "OUTLINE" label, 13px rows, `#FFF6D6` active highlight). Legacy = the
 * shipped white rail, byte-identical when `task_editorial_ui` is off.
 */

import { useMapTheme } from "../theme/MapThemeContext";
import { type TreeNode, flattenTree } from "../tree/buildTree";
import { StatusDot } from "./StatusChip";

/** The editorial active-row highlight (`tarii.css` `.t-outline .row.active`). */
const EDITORIAL_ACTIVE_BG = "#FFF6D6";

export function TaskOutlineRail({
  roots,
  selectedId,
  onSelect,
  onAddSubtask,
}: {
  roots: TreeNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onAddSubtask: () => void;
}) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const rows = flattenTree(roots);
  return (
    <div
      data-testid="outline-rail"
      className="flex h-full w-56 shrink-0 flex-col border-r"
      style={{
        backgroundColor: editorial ? c.canvas : "#FFFFFF",
        borderColor: editorial ? c.rule : c.softBorder,
      }}
    >
      <div
        className="px-4 pt-4 pb-2 text-[10px] font-bold uppercase tracking-wider"
        style={{
          color: editorial ? c.muted : "#6B7280",
          fontFamily: editorial ? theme.fonts.mono : undefined,
          fontWeight: editorial ? 500 : undefined,
          letterSpacing: editorial ? "0.1em" : undefined,
        }}
      >
        Outline
      </div>
      <ul className="flex-1 overflow-auto px-2 pb-2">
        {rows.map((node: TreeNode) => {
          const active = node.id === selectedId;
          return (
            <li key={node.id}>
              <button
                type="button"
                data-testid="outline-row"
                data-node-id={node.id}
                aria-current={active}
                onClick={() => onSelect(node.id)}
                className={`flex w-full items-center gap-1.5 rounded px-2 py-1 text-left transition-colors ${
                  editorial ? "text-[13px]" : "text-xs"
                }`}
                style={{
                  paddingLeft: 8 + node.depth * 14,
                  backgroundColor: active
                    ? editorial
                      ? EDITORIAL_ACTIVE_BG
                      : c.rollupBg
                    : "transparent",
                  color: node.depth === 0 ? c.ink : c.slate,
                  fontWeight: node.depth === 0 ? (editorial ? 500 : 600) : 400,
                  fontFamily: editorial ? theme.fonts.sans : undefined,
                }}
              >
                <StatusDot state={node.state} />
                <span className="truncate">{node.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div
        className="border-t px-3 py-2"
        style={{ borderColor: editorial ? c.rule : c.cardBorder }}
      >
        <button
          type="button"
          data-testid="rail-add-subtask"
          onClick={onAddSubtask}
          className="text-xs transition-opacity hover:opacity-70"
          style={{ color: editorial ? c.teal : c.teal }}
        >
          + sub-task
        </button>
      </div>
    </div>
  );
}
